import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Flex, Select } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { collectExpressions } from '../required-expression'
import { useSegmentValueInputContext } from './segment-value-input-context'

export interface PageTagSelectProps {
    id: string
    placeholder: string
    /** The property whose stored tags have to stay renderable. */
    property: string
    value: string
    onChange: (next: string) => void
    disabled?: boolean
    size?: 'small' | 'middle' | 'large'
    getPopupContainer?: () => HTMLElement
}

/** How long the reader is taken to still be scrolling after they last did something. */
const SCROLL_INTENT_MS = 100

/** How close to the end of the list the reader must be before the next page is read. */
const NEAR_END_PX = 500

/** How long typing settles before it is treated as a search. */
const SEARCH_DEBOUNCE_MS = 320

interface Listing {
    names: string[]
    next: string | null
}

const EMPTY_LISTING: Listing = { names: [], next: null }

/**
 * Which of the two lists a read is filling, and so whose read it is.
 *
 * The browsed list is the component's own — it asks for the first page before the
 * reader has done anything, and for the next as they scroll. A search is the
 * reader's, started by what they typed. Only the owner of a read may abandon it.
 */
type ReadOwner = 'browsing' | 'searching'

function ownerOf(request: { search?: string }): ReadOwner {
    return request.search === undefined ? 'browsing' : 'searching'
}

/**
 * The page tag a condition counts visits to.
 *
 * A domain's tag list is far too long to send at once, so the list here is read a
 * page at a time — the first eagerly, the rest as the reader scrolls, and a narrowed
 * one whenever they type. The browsed list and a search are separate reads: a search
 * the reader supersedes by typing on, or takes back by emptying the box, is abandoned
 * so it cannot answer over the list they have been returned to, while the browsed
 * list stays on its way — they never asked for that one to stop.
 *
 * Whatever the server sends, the tags the segment itself already names are always in
 * the list. A stored tag deep enough in the list never to be fetched would otherwise
 * leave the row blank, and the next save would drop what the condition was counting.
 */
export function PageTagSelect({
    id,
    placeholder,
    property,
    value,
    onChange,
    disabled,
    size,
    getPopupContainer,
}: PageTagSelectProps) {
    const { pageTags, getDocument } = useSegmentValueInputContext()

    const [browsing, setBrowsing] = useState<Listing>(EMPTY_LISTING)
    const [searching, setSearching] = useState<(Listing & { term: string }) | null>(null)
    const [typed, setTyped] = useState('')
    const [reading, setReading] = useState(false)
    // Held rather than left to the select so that recording a new tag can close the
    // list: that choice is made on the empty-list panel, which the select does not
    // treat as a selection.
    const [open, setOpen] = useState(false)

    // The read in flight for each list, if any. Holding them is what makes the
    // newest read of a list the only one that can land — and what lets an abandoned
    // one release its connection instead of running to completion unread. A slot
    // each rather than one between them, because a read is only ever abandoned by
    // whoever started it.
    const inFlight = useRef<Record<ReadOwner, AbortController | null>>({ browsing: null, searching: null })
    /**
     * Whether a page of the browsed list has ever landed.
     *
     * While this is false the list is still owed: a read that failed leaves no names
     * and no cursor to page from, so scrolling cannot ask again and nothing else
     * would. Opening the list asks again rather than showing the reader an emptiness
     * the server never stated.
     */
    const listingLanded = useRef(false)
    const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
    /**
     * When a scroll would still count as the reader's own.
     *
     * The option list emits scroll events of its own as filtering re-flows it, and
     * paging on one of those reads the list out from under the reader. Only a scroll
     * that closely follows a wheel or an arrow key is theirs, so the window is short
     * enough to have closed by the next keystroke.
     */
    const scrollIntentUntil = useRef(0)

    const read = useCallback(
        async (request: { search?: string; cursor?: string }, append: boolean) => {
            if (!pageTags) return

            const owner = ownerOf(request)
            inFlight.current[owner]?.abort()
            const controller = new AbortController()
            inFlight.current[owner] = controller
            setReading(true)

            try {
                const page = await pageTags.read({ ...request, signal: controller.signal })

                if (controller.signal.aborted) return

                if (request.search === undefined) {
                    listingLanded.current = true
                    setBrowsing((previous) => ({
                        names: append ? [...previous.names, ...page.names] : page.names,
                        next: page.next,
                    }))
                } else {
                    const term = request.search
                    setSearching((previous) => ({
                        term,
                        names: append && previous?.term === term ? [...previous.names, ...page.names] : page.names,
                        next: page.next,
                    }))
                }
            } catch {
                // A read that failed leaves the list as it was: the tags already on
                // screen are still real, and blanking them would take away the stored
                // one the reader came here to keep.
            } finally {
                if (inFlight.current[owner] === controller) {
                    inFlight.current[owner] = null
                    // The other list may still be reading, and the control goes on
                    // saying so until neither is.
                    setReading(Boolean(inFlight.current.browsing || inFlight.current.searching))
                }
            }
        },
        [pageTags],
    )

    // The first page is read before the reader asks for anything, so the list is
    // populated the moment they open it.
    useEffect(() => {
        read({}, false)

        return () => {
            if (debounce.current) clearTimeout(debounce.current)
            inFlight.current.browsing?.abort()
            inFlight.current.searching?.abort()
        }
    }, [read])

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setOpen(next)

            // Opening the list is the reader asking to see it, and so the moment to
            // make good on a list that was asked for but never arrived.
            if (next && !listingLanded.current && !inFlight.current.browsing) read({}, false)
        },
        [read],
    )

    const handleSearch = useCallback(
        (term: string) => {
            if (debounce.current) clearTimeout(debounce.current)
            setTyped(term)

            const trimmed = term.trim()

            if (!trimmed) {
                // Emptying the box takes the search back, so the read it started is
                // abandoned too. Left running it would answer over the browsed list
                // the reader has been returned to, and hold its connection to do it.
                // The read is disowned here rather than when it gets around to
                // settling, so the list stops saying it is searching and the reader
                // can page the browsed list again straight away.
                //
                // Only the search goes. The browsed list is not the reader's to take
                // back, so a character typed and removed before the debounce asked
                // the server anything leaves the list on its way to them.
                inFlight.current.searching?.abort()
                inFlight.current.searching = null
                setReading(Boolean(inFlight.current.browsing))
                setSearching(null)
                return
            }

            debounce.current = setTimeout(() => read({ search: trimmed }, false), SEARCH_DEBOUNCE_MS)
        },
        [read],
    )

    const armScroll = useCallback(() => {
        scrollIntentUntil.current = Date.now() + SCROLL_INTENT_MS
    }, [])

    const handlePopupScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (Date.now() > scrollIntentUntil.current) return
            // A page read into a list that is about to be replaced is wasted, so
            // paging waits for whatever is reading — either list — to answer first.
            if (inFlight.current.browsing || inFlight.current.searching) return

            const list = event.currentTarget
            const depthTillEnd = list.scrollHeight - list.scrollTop - list.getBoundingClientRect().height
            if (depthTillEnd > NEAR_END_PX) return

            const cursor = searching ? searching.next : browsing.next
            if (!cursor) return

            read({ search: searching?.term, cursor }, true)
        },
        [read, searching, browsing.next],
    )

    const options = useMemo(() => {
        const fromServer = searching ? searching.names : browsing.names
        const term = searching?.term.toLowerCase()

        // Everything the segment already names, the row's own tag included.
        const known = collectExpressions(getDocument?.(), property)
        if (value && !known.includes(value)) known.push(value)

        // A narrowed list stays narrow. Answering a search with tags the reader did
        // not ask about buries what they did ask for, and leaves no empty list for
        // the offer to record a name that does not exist yet.
        const relevant = term ? known.filter((tag) => tag.toLowerCase().includes(term)) : known

        return [...new Set([...fromServer, ...relevant])].sort().map((name) => ({ label: name, value: name }))
    }, [searching, browsing.names, getDocument, property, value])

    const recordTyped = useCallback(async () => {
        const name = typed.trim()
        if (!pageTags || !name) return

        const stored = await pageTags.create(name)
        if (!stored) return

        setTyped('')
        setSearching(null)
        setOpen(false)
        onChange(stored)
    }, [pageTags, typed, onChange])

    return (
        <Select
            id={id}
            className="segment-page-tag-select"
            showSearch={{ onSearch: handleSearch, filterOption: false }}
            placeholder={placeholder}
            loading={reading}
            size={size}
            disabled={disabled}
            value={value === '' ? undefined : value}
            options={options}
            onChange={onChange}
            open={open}
            onOpenChange={handleOpenChange}
            onPopupScroll={handlePopupScroll}
            onInputKeyDown={(event) => {
                if (event.key === 'ArrowDown') armScroll()
            }}
            popupRender={(menu) => (
                <div
                    onWheel={(event) => {
                        if (event.deltaY > 0) armScroll()
                    }}
                >
                    {menu}
                </div>
            )}
            notFoundContent={
                reading ? (
                    <Flex gap="small" align="center" justify="center">
                        <LoadingOutlined spin={true} aria-hidden="true" />
                        <span>Searching</span>
                    </Flex>
                ) : typed.trim() ? (
                    <Button
                        className="segment-page-tag-create"
                        type="link"
                        // The empty-results slot of a Select is not a focus stop, so the reader reaches
                        // this by pointer. A Button is still what it is: hand-rolling the role and the
                        // tab stop onto a div reimplemented what this already carries, minus the
                        // keyboard and disabled handling that comes with it.
                        onClick={recordTyped}
                    >
                        Create New Page Tag: {typed}
                    </Button>
                ) : null
            }
            getPopupContainer={getPopupContainer}
        />
    )
}
